/**
 * View 2, HIPO profile & growth: who the high-potentials are (boxes 1, 2 and 3),
 * where they sit, and whether they are being grown. Gender and race appear only
 * as totals (HR, 9 Oct 2026). The deck's "accelerated development journey" is
 * left out: nothing records it, and the people management forum monitors it.
 */
import React, { useMemo } from 'react'
import type { ReportingPerson } from '@/services/api/talentReportingService'
import {
  PERFORMANCE_SHORT,
  POTENTIAL_SHORT,
} from '@/components/performance/manager/talent-review/eduvosBlocks'
import { BarList, Panel, PeopleTable, Tile, TileRow, type PeopleColumn } from './TalentDashboardParts'
import {
  CRITICAL_NO,
  CRITICAL_YES,
  LAYER_ORDER,
  MOBILITY_ORDER,
  READINESS_SHORT,
  count,
  countBy,
  fmtPct,
  gradesHighestFirst,
  hipoGrowth,
  mobilityOf,
  roleOf,
} from './talentDashboardData'

export const criticalOf = (p: ReportingPerson) =>
  p.critical_role === null ? null : p.critical_role ? CRITICAL_YES : CRITICAL_NO

const successorText = (p: ReportingPerson) =>
  p.successor_for.length ? p.successor_for.map((s) => s.role).join('; ') : 'No'

export const HIPO_COLUMNS: PeopleColumn[] = [
  { key: 'name', label: 'Name', render: (p) => p.name, sortValue: (p) => p.name },
  { key: 'role', label: 'Role', render: (p) => roleOf(p) || '', sortValue: (p) => roleOf(p) || '' },
  { key: 'function', label: 'Function', render: (p) => p.function || '', sortValue: (p) => p.function || '' },
  { key: 'layer', label: 'Leadership layer', render: (p) => p.leadership_layer || '', sortValue: (p) => p.leadership_layer || '' },
  { key: 'grade', label: 'Grade', render: (p) => p.grade || '', sortValue: (p) => p.grade || '' },
  { key: 'box', label: 'Box', render: (p) => p.block ?? '', sortValue: (p) => p.block ?? 99, className: 'text-center' },
  { key: 'impact', label: 'Impact', render: (p) => (p.performance ? PERFORMANCE_SHORT[p.performance] : '') },
  { key: 'potential', label: 'Potential', render: (p) => (p.potential ? POTENTIAL_SHORT[p.potential] : '') },
  { key: 'critical', label: 'Critical role', render: (p) => (p.critical_role ? 'Yes' : 'No'), sortValue: (p) => (p.critical_role ? 0 : 1) },
  { key: 'successor', label: 'Successor for', render: successorText, sortValue: (p) => (p.is_successor ? 0 : 1) },
  { key: 'next', label: 'Next role', render: (p) => p.next_role || '', sortValue: (p) => p.next_role || '' },
  {
    key: 'readiness',
    label: 'Readiness',
    render: (p) => (p.next_role_readiness ? READINESS_SHORT[p.next_role_readiness] : ''),
    sortValue: (p) => p.next_role_readiness || 'z',
  },
  { key: 'plan', label: 'Growth plan', render: (p) => (p.has_growth_plan ? 'Yes' : 'No'), sortValue: (p) => (p.has_growth_plan ? 0 : 1) },
  { key: 'focus', label: 'Growth focus', render: (p) => p.growth_focus || '', sortValue: (p) => p.growth_focus || '' },
]

export const HipoView: React.FC<{
  people: ReportingPerson[]
  onExport?: (rows: ReportingPerson[], what: string) => void
  /** Gender and race: administrators and HR administrators only. */
  showDemographics?: boolean
}> = ({ people, onExport, showDemographics = true }) => {
  const hipos = useMemo(() => people.filter((p) => p.is_hipo), [people])
  const growth = useMemo(() => hipoGrowth(people), [people])
  const byBox = [1, 2, 3].map((b) => hipos.filter((p) => p.block === b).length)
  const mobility = countBy(hipos, mobilityOf, MOBILITY_ORDER)
  const open = hipos.filter((p) => p.mobility === 'yes' || p.mobility === 'maybe').length

  return (
    <div className="space-y-4">
      <TileRow min="150px">
        <Tile label="High-potentials" value={growth.hipos} sub={`${fmtPct(growth.hipos, people.length)} of those in scope`} />
        {byBox.map((n, i) => (
          <Tile key={i} label={`Box ${i + 1}`} value={n} sub={`${fmtPct(n, growth.hipos)} of high-potentials`} />
        ))}
        <Tile label="Open to moving" value={fmtPct(open, growth.hipos)} sub={mobility.map((m) => `${m.label} ${m.count}`).join(' · ') || 'Nobody yet'} />
      </TileRow>

      <Panel title="Are we growing them?" note={`Of the ${growth.hipos} high-potentials.`}>
        <TileRow min="170px">
          <Tile label="Have a Personalised Growth plan" value={fmtPct(growth.withPlan, growth.hipos)} sub={count(growth.withPlan, 'person', 'people')} />
          <Tile label="Next role planned in 1 to 3 years" value={fmtPct(growth.movePlanned, growth.hipos)} sub={count(growth.movePlanned, 'person', 'people')} />
          <Tile label="Named a successor" value={fmtPct(growth.successors, growth.hipos)} sub={`${count(growth.successors, 'person', 'people')}, for a People Leader's role`} />
          <Tile label="Ready now for their next role" value={fmtPct(growth.readyNow, growth.hipos)} sub={count(growth.readyNow, 'person', 'people')} />
        </TileRow>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="By function"><BarList rows={countBy(hipos, (p) => p.function)} /></Panel>
        <Panel title="By leadership layer"><BarList rows={countBy(hipos, (p) => p.leadership_layer, LAYER_ORDER)} /></Panel>
        <Panel title="By grade"><BarList rows={countBy(hipos, (p) => p.grade, gradesHighestFirst(hipos))} /></Panel>
        <Panel title="By mobility"><BarList rows={mobility} /></Panel>
      </div>

      <Panel
        title="Profile"
        note={showDemographics ? "Totals only. Nobody's gender or race is shown or downloaded by name." : 'Gender and race are shown to administrators and HR administrators.'}
      >
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {showDemographics && (
            <>
              <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Gender</h3><BarList rows={countBy(hipos, (p) => p.gender)} /></div>
              <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Race</h3><BarList rows={countBy(hipos, (p) => p.race)} /></div>
            </>
          )}
          <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Nationality</h3><BarList rows={countBy(hipos, (p) => p.nationality)} max={8} /></div>
          <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Employment type</h3><BarList rows={countBy(hipos, (p) => p.employment_category)} /></div>
          <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Critical role</h3><BarList rows={countBy(hipos, criticalOf, [CRITICAL_YES, CRITICAL_NO])} /></div>
        </div>
      </Panel>

      <PeopleTable
        title="High-potentials and their growth"
        people={hipos}
        columns={HIPO_COLUMNS}
        onExport={onExport && (() => onExport(hipos, 'High-potentials'))}
      />
    </div>
  )
}
